/**
 * JurisdictionApplicabilityService — ADR-0024 §4/§5 (V3-4-3).
 *
 * The engine half of "jurisdiction applicability is computed per AI
 * system from the intake classification, never asserted per workspace".
 * Rules are editorial content (`jurisdiction_rules`, migration 068);
 * this module is the code that evaluates them.
 *
 *   computeApplicability(classification, rules, { as_of })
 *     → { applicable: [...], conflicts: [...] }
 *
 * Properties this module guarantees, because signed assessment packages
 * depend on them (ADR-0010 / ADR-0014) and UC-D's acceptance requires
 * determinism:
 *   - Pure. No database, no network, no LLM, no `eval`. The only
 *     non-deterministic input is the `as_of` default (today), which
 *     every caller that pins a result MUST pass explicitly.
 *   - Deterministic ordering: `applicable` sorts by (jurisdiction,
 *     statute, citation, rule_id); `conflicts` sorts by both sides'
 *     (jurisdiction, statute) then kind. Input order never leaks out.
 *   - No input mutation.
 *   - Never resolves a conflict silently: both rules stay in
 *     `applicable` with both citations, and the stricter one is named
 *     as a `recommendation` (ADR-0024 §5, owner decision 3) — never a
 *     determination.
 *
 * This is NOT an exception to ADR-0001: predicates are structured
 * booleans over the frozen intake vocabulary, not natural-language
 * matching.
 */
import { jurisdictionToRegion } from '../utils/jurisdiction-codes.js';
// ============================================================
// Frozen vocabulary — ADR-0024 §1 (owner decision 1)
// ============================================================
// Anything a rule needs beyond this list is a change to intake
// (migration 070 + UseCaseIntake.tsx) AND to ADR-0024 — never a
// free-text predicate. `validatePredicate` below is the enforcement.
/** Boolean deployment-context flags. Absent → false. */
export const BOOLEAN_FIELDS = [
    'employment_decision',
    'consequential_decision',
    'credit_decision',
    'healthcare_decision',
    'law_enforcement',
    'critical_infrastructure',
    'biometric_identification',
    'social_scoring',
    'subliminal_manipulation',
    'exploits_vulnerabilities',
    // V3-4-8 (ADR-0024 §1 amendment 2026-09-08): 'true' when the
    // deployer or provider is a government / public authority, so rules
    // that only bite public-sector use (e.g. TRAIGA's government-entity
    // prohibitions, EU AI Act public-sector overlays) can be expressed
    // structurally instead of approximated.
    'public_authority',
    // V112-2-12 (ADR-0024 §1 amendment 2026-10-22, BL-48): a federal contract passes the OMB AI acquisition terms
    // (M-25-22 §4(d), M-26-04) down to this system. Profile default (migration 756), system override.
    'federal_flowdown',
];
/** Single-valued fields. `gpai_role` defaults to 'none' when absent. */
export const SCALAR_FIELDS = [
    'gpai_role',
    'autonomy_level',
    'taxonomy_type',
    // V6-1-4 / ADR-0034 §4.1: intake vocabulary v2026.09
    'decision_role',
    'human_oversight_mode',
    'sector',
    'deployment_target',
    // V112-2-12 (ADR-0024 §1 amendment 2026-10-22, BL-48): who deploys the system in a US-federal context, one of
    // DEPLOYER_TYPES. Agency-only duties (OMB M-25-21) key on 'federal_agency'. Absent = not answered.
    'deployer_type',
];
/** V112-2-12: the `deployer_type` values (migration 756 CHECK). */
export const DEPLOYER_TYPES = ['federal_agency', 'government_contractor', 'other'];
/** Multi-valued fields. Absent → empty list. Only `contains` applies. */
export const ARRAY_FIELDS = [
    'affected_persons',
    'data_classes',
    'deployment_jurisdictions',
    // V6-1-4 / ADR-0034 §4.1: intake vocabulary v2026.09
    'data_categories',
    'affected_parties',
    'embedded_third_party_ai',
    // V86-1-9 / ADR-0024 §1 amendment: where AI outputs land and where
    // affected persons are located. `output_destinations` already has a DB
    // column (`output_destinations_json` on `ai_systems`); both fields
    // were missing from the engine vocabulary.
    'output_destinations',
    'affected_person_locations',
    // V94-5-1 / ADR-0042 D-94-2: NIST AI 200-1 activity categories.
    // Rules can predicate on what an AI system DOES (generate, retrieve,
    // detect, etc.), not just what data it touches.
    'ai_activity',
];
export const VOCABULARY = [
    ...BOOLEAN_FIELDS,
    ...SCALAR_FIELDS,
    ...ARRAY_FIELDS,
];
export const PREDICATE_OPERATORS = [
    'and',
    'or',
    'not',
    'eq',
    'in',
    'contains',
    // V3-4-8: 'true' when an array field is non-empty. Enables rules
    // that key on "the system interacts with natural persons" (EU
    // Art. 50) or "video_interview is among the data classes"
    // (Illinois AIVIA) without inventing a boolean-per-value.
    'non_empty',
];
export const TIER_RANK = {
    minimal_risk: 1,
    limited_risk: 2,
    high_risk: 3,
    unacceptable: 4,
};
/** ADR-0077 §1: the canonical tiers, strictest first. Frozen code. */
export const RULE_TIERS = ['unacceptable', 'high_risk', 'limited_risk', 'minimal_risk'];
/**
 * ADR-0077 §1: the canonical actor roles. Frozen code: a jurisdiction's own
 * words ("developer", "employer") are reviewed terms that each map to exactly
 * one of these (jurisdiction-term-service). An obligation's `scope` and a
 * system's `actor_roles` hold only these values. Adding one is a code change
 * and an amendment to ADR-0077.
 */
export const CANONICAL_ROLES = ['provider', 'deployer', 'importer', 'distributor', 'authorized_representative'];
export function isCanonicalRole(v) {
    return typeof v === 'string' && CANONICAL_ROLES.includes(v);
}
export function isRuleTier(v) {
    return typeof v === 'string' && RULE_TIERS.includes(v);
}
/**
 * ADR-0077 §5: a drafter's `scope` reduced to canonical roles. The legacy
 * "both" means provider and deployer; anything else that is not canonical is
 * dropped. Returns undefined when nothing canonical is left.
 */
export function canonicalScope(raw) {
    if (!Array.isArray(raw))
        return undefined;
    const out = [];
    for (const v of raw) {
        const s = typeof v === 'string' ? v.trim().toLowerCase() : '';
        const roles = s === 'both' ? ['provider', 'deployer'] : isCanonicalRole(s) ? [s] : [];
        for (const r of roles)
            if (!out.includes(r))
                out.push(r);
    }
    return out.length ? out : undefined;
}
export function legalStandingOf(rule) {
    if (rule.legal_status === 'vacated' || rule.legal_status === 'annulled')
        return 'struck';
    if (rule.legal_status === 'stayed' || rule.preemption_risk === 'high')
        return 'contested';
    return 'in_force';
}
// ============================================================
// computeApplicability
// ============================================================
export function computeApplicability(classification, rules, options = {}) {
    const asOf = toDate(options.as_of ?? new Date().toISOString());
    const deploymentJurisdictions = normalizeJurisdictionList(readArray(classification, 'deployment_jurisdictions'));
    // v9.2 / D-92-3: deduped coverage-region filter set (empty = no filter).
    const coverageRegions = new Set(options.coverage_regions ?? []);
    // ADR-0077 §4: the system's confirmed role(s). Unknown values are ignored.
    const actorRoles = readActorRoles(classification);
    const applicable = [];
    const notApplicable = [];
    const decisionNeeded = [];
    for (const rule of rules) {
        if (rule.status !== 'active')
            continue;
        // v11.7 / ADR-0111 §2: a law struck by a court never drives a control.
        const standing = legalStandingOf(rule);
        if (standing === 'struck') {
            notApplicable.push({ rule_id: rule.rule_id, reason: `The law was ${rule.legal_status} by a court${rule.preemption_note ? ` (${rule.preemption_note})` : ''}` });
            continue;
        }
        // v9.2 / D-92-3: region coverage filter. Region comes from the rule's
        // `region` column, falling back to a deterministic derivation from the
        // jurisdiction code so pre-171 rows still filter correctly.
        if (coverageRegions.size > 0) {
            const region = rule.region ?? jurisdictionToRegion(rule.jurisdiction);
            if (!coverageRegions.has(region)) {
                notApplicable.push({
                    rule_id: rule.rule_id,
                    reason: `Outside covered regions (rule region ${region})`,
                });
                continue;
            }
        }
        const effective = toDate(rule.effective_date);
        if (effective > asOf) {
            notApplicable.push({ rule_id: rule.rule_id, reason: `Not yet in force (effective ${rule.effective_date})` });
            continue;
        }
        const sunset = rule.sunset_date ? toDate(rule.sunset_date) : null;
        if (sunset !== null && sunset <= asOf) {
            notApplicable.push({ rule_id: rule.rule_id, reason: `Sunset on ${rule.sunset_date}` });
            continue;
        }
        const scoped = jurisdictionInScope(rule.jurisdiction, deploymentJurisdictions);
        if (!scoped.inScope)
            continue;
        let evaluated;
        try {
            evaluated = evaluate(rule.predicates, classification);
        }
        catch (err) {
            throw new Error(`Rule ${rule.rule_id} (${rule.jurisdiction} ${rule.statute}) has an invalid predicate: ${err instanceof Error ? err.message : String(err)}`);
        }
        if (!evaluated.value)
            continue;
        // V104-G-11: a duty with its own start date is reported only from that date.
        // The rule's date is the earliest, so this only drops later phases.
        const inForce = rule.obligations.filter((o) => !o.effective_date || toDate(o.effective_date) <= asOf);
        if (inForce.length === 0 && rule.obligations.length > 0) {
            const first = rule.obligations.map((o) => toDate(o.effective_date)).sort()[0];
            notApplicable.push({ rule_id: rule.rule_id, reason: `No duty in force yet (first duty starts ${first})` });
            continue;
        }
        const pending = rule.obligations.length - inForce.length;
        // ADR-0077 §4: keep only the duties for the system's confirmed role. A duty
        // with no `scope` applies to every role. Unanswered = nothing is trimmed.
        const scopedDuties = inForce.some((o) => (o.scope ?? []).length > 0);
        const forRole = actorRoles.length === 0
            ? inForce
            : inForce.filter((o) => !o.scope || o.scope.length === 0 || o.scope.some((r) => actorRoles.includes(r)));
        if (inForce.length > 0 && forRole.length === 0) {
            notApplicable.push({ rule_id: rule.rule_id, reason: `No duties for your role (${actorRoles.join(', ')})` });
            continue;
        }
        const rationale = [...evaluated.reasons];
        if (scoped.reason)
            rationale.push(scoped.reason);
        rationale.push(`In force since ${rule.effective_date}${rule.sunset_date ? ` (sunsets ${rule.sunset_date})` : ''}`);
        if (pending > 0) {
            rationale.push(`${pending} later-phase ${pending === 1 ? 'duty' : 'duties'} not yet in force`);
        }
        if (scopedDuties) {
            if (actorRoles.length === 0) {
                rationale.push('Your role for this system is not answered: every duty is shown');
            }
            else {
                const trimmed = inForce.length - forRole.length;
                rationale.push(`Duties for your role (${actorRoles.join(', ')})` +
                    (trimmed > 0 ? `: ${trimmed} ${trimmed === 1 ? 'duty' : 'duties'} for other roles left out` : ''));
            }
        }
        // v11.7 / ADR-0111 §7 (D-116-14): a contested law applies only by the workspace's choice.
        if (standing === 'contested') {
            const choice = options.rule_choices?.[rule.rule_id];
            if (choice === 'does_not_apply') {
                notApplicable.push({ rule_id: rule.rule_id, reason: 'Contested law: your workspace decided it does not apply' });
                continue;
            }
            if (choice !== 'applies') {
                decisionNeeded.push({
                    rule_id: rule.rule_id, jurisdiction: rule.jurisdiction, statute: rule.statute, citation: rule.citation,
                    legislation_id: rule.legislation_id ?? null, law_title: rule.law_title ?? null, legal_status: rule.legal_status ?? null,
                    stay_kind: rule.stay_kind ?? null, preemption_risk: rule.preemption_risk ?? null, preemption_note: rule.preemption_note ?? null,
                });
                continue;
            }
            rationale.push(`Contested law (${rule.legal_status === 'stayed' ? 'enforcement stayed' : 'high preemption risk'}): applies by your workspace's decision`);
        }
        // v9.2 / D-92-3: non-binding (multilateral) rules surface as advisory.
        const advisory = rule.binding === 'non_binding';
        if (advisory) {
            rationale.push('Advisory (non-binding instrument) — informational, no SLA');
        }
        const tierOf = ruleTierWithTerm(forRole);
        applicable.push({
            rule_id: rule.rule_id,
            jurisdiction: rule.jurisdiction,
            statute: rule.statute,
            citation: rule.citation,
            tier: tierOf.tier,
            ...(tierOf.tier_term ? { tier_term: tierOf.tier_term } : {}),
            obligations: forRole.map((o) => ({ ...o })),
            rationale,
            effective_date: rule.effective_date,
            sunset_date: rule.sunset_date,
            strictness_rank: rule.strictness_rank,
            source_refs: rule.source_refs.map((s) => ({ ...s })),
            instrument_version: rule.instrument_version,
            ...(advisory ? { advisory: true } : {}),
        });
    }
    applicable.sort(compareApplicable);
    // v9.2 / D-92-3: advisory (non-binding) rules surface in `applicable`
    // but must not drive conflict resolution or override a binding rule.
    const bindingApplicable = applicable.filter((r) => !r.advisory);
    return { applicable, conflicts: detectConflicts(bindingApplicable), not_applicable: notApplicable, decision_needed: decisionNeeded };
}
/**
 * Evaluate one predicate against a classification. Exported so the
 * editorial queue can preview "would this rule have matched?" on a
 * sample system before an editor approves it.
 */
export function evaluatePredicate(predicate, classification) {
    const r = evaluate(predicate, classification);
    return { matched: r.value, rationale: r.reasons };
}
function evaluate(predicate, c) {
    if (predicate === null || typeof predicate !== 'object' || Array.isArray(predicate)) {
        throw new Error(`predicate must be an object, got ${JSON.stringify(predicate)}`);
    }
    const keys = Object.keys(predicate);
    if (keys.length !== 1) {
        throw new Error(`predicate must have exactly one operator key, got [${keys.join(', ')}]`);
    }
    const op = keys[0];
    if (op === 'and') {
        const children = predicate.and;
        assertNonEmptyArray(children, 'and');
        const reasons = [];
        for (const child of children) {
            const r = evaluate(child, c);
            if (!r.value)
                return { value: false, reasons: [] };
            reasons.push(...r.reasons);
        }
        return { value: true, reasons };
    }
    if (op === 'or') {
        const children = predicate.or;
        assertNonEmptyArray(children, 'or');
        for (const child of children) {
            const r = evaluate(child, c);
            // First satisfied branch wins, so the rationale is stable
            // regardless of how many branches happen to be true.
            if (r.value)
                return { value: true, reasons: r.reasons };
        }
        return { value: false, reasons: [] };
    }
    if (op === 'not') {
        const child = predicate.not;
        const r = evaluate(child, c);
        if (r.value)
            return { value: false, reasons: [] };
        return { value: true, reasons: [`not (${describePredicate(child)})`] };
    }
    if (op === 'eq') {
        const { field, value } = predicate.eq;
        assertScalarField(field, 'eq');
        const actual = readScalar(c, field);
        const expected = coerceForField(field, value);
        const matched = actual === expected;
        return matched
            ? { value: true, reasons: [`${field} = ${format(expected)}`] }
            : { value: false, reasons: [] };
    }
    if (op === 'in') {
        const { field, values } = predicate.in;
        assertScalarField(field, 'in');
        assertNonEmptyArray(values, 'in.values');
        const actual = readScalar(c, field);
        const expected = values.map((v) => coerceForField(field, v));
        const matched = expected.some((v) => v === actual);
        return matched
            ? { value: true, reasons: [`${field} = ${format(actual)} (one of ${expected.map(format).join(', ')})`] }
            : { value: false, reasons: [] };
    }
    if (op === 'contains') {
        const { field, value } = predicate.contains;
        if (!ARRAY_FIELDS.includes(field)) {
            throw new Error(`unknown predicate: contains expects one of [${ARRAY_FIELDS.join(', ')}], got '${field}'`);
        }
        const actual = readArray(c, field);
        const matched = actual.includes(value);
        return matched
            ? { value: true, reasons: [`${field} contains ${format(value)}`] }
            : { value: false, reasons: [] };
    }
    // V3-4-8: 'non_empty' — array field is non-empty. Used by EU AI Act
    // Art. 50 (interacts directly with natural persons -> affected_persons
    // non-empty) and by rules that key on presence of any deployment
    // jurisdiction. Only accepts array fields; use `not(eq, false)` for
    // booleans and `not(eq, '')` for scalars.
    if (op === 'non_empty') {
        const { field } = predicate.non_empty;
        if (!ARRAY_FIELDS.includes(field)) {
            throw new Error(`unknown predicate: non_empty expects one of [${ARRAY_FIELDS.join(', ')}], got '${field}'`);
        }
        const actual = readArray(c, field);
        return actual.length > 0
            ? { value: true, reasons: [`${field} is non-empty (${actual.length} value${actual.length === 1 ? '' : 's'})`] }
            : { value: false, reasons: [] };
    }
    throw new Error(`unknown predicate: '${op}' is not one of [${PREDICATE_OPERATORS.join(', ')}]`);
}
/** Flat, parenthesised rendering of a predicate — used by `not` traces. */
export function describePredicate(predicate) {
    const keys = Object.keys(predicate ?? {});
    const op = keys[0];
    switch (op) {
        case 'and':
            return predicate.and.map(describePredicate).join(' and ');
        case 'or':
            return predicate.or.map(describePredicate).join(' or ');
        case 'not':
            return `not (${describePredicate(predicate.not)})`;
        case 'eq': {
            const { field, value } = predicate.eq;
            return `${field} = ${format(value)}`;
        }
        case 'in': {
            const { field, values } = predicate.in;
            return `${field} in [${values.map(format).join(', ')}]`;
        }
        case 'contains': {
            const { field, value } = predicate.contains;
            return `${field} contains ${format(value)}`;
        }
        case 'non_empty': {
            const { field } = predicate.non_empty;
            return `${field} is non-empty`;
        }
        default:
            return `<invalid predicate: ${op ?? 'empty'}>`;
    }
}
function assertNonEmptyArray(value, what) {
    if (!Array.isArray(value) || value.length === 0) {
        throw new Error(`${what} requires a non-empty array of predicates`);
    }
}
function assertScalarField(field, op) {
    if (ARRAY_FIELDS.includes(field)) {
        throw new Error(`unknown predicate: '${field}' is a multi-valued field — use 'contains', not '${op}'`);
    }
    const allowed = [...BOOLEAN_FIELDS, ...SCALAR_FIELDS];
    if (!allowed.includes(field)) {
        throw new Error(`unknown predicate: '${field}' is not in the ADR-0024 §1 vocabulary [${VOCABULARY.join(', ')}]`);
    }
}
// ---- field readers ----------------------------------------------------
/**
 * The three ADR-0024 §1 additions may arrive at the top level (V3-3-3's
 * `ai_use_cases` columns) or nested in `deployment_context` (the intake
 * form's existing shape). The ten legacy booleans live in
 * `deployment_context`; `taxonomy_type` / `data_classes` /
 * `affected_persons` / `autonomy_level` live at the top level. One
 * lookup order covers all of them.
 */
function readRaw(c, field) {
    const top = c[field];
    if (top !== undefined)
        return top;
    const ctx = c.deployment_context;
    return ctx?.[field];
}
function readScalar(c, field) {
    const raw = readRaw(c, field);
    if (BOOLEAN_FIELDS.includes(field))
        return Boolean(raw);
    if (field === 'gpai_role')
        return raw ?? 'none';
    return raw === undefined || raw === null ? undefined : raw;
}
/** ADR-0077 §4: canonical roles from `actor_roles` (top level only); anything else is ignored. */
function readActorRoles(c) {
    const raw = c.actor_roles;
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const v of raw)
        if (isCanonicalRole(v) && !out.includes(v))
            out.push(v);
    return out;
}
function readArray(c, field) {
    const raw = readRaw(c, field);
    if (!Array.isArray(raw))
        return [];
    return raw.filter((v) => typeof v === 'string');
}
function coerceForField(field, value) {
    if (BOOLEAN_FIELDS.includes(field))
        return Boolean(value);
    return value;
}
function format(value) {
    return typeof value === 'string' ? `'${value}'` : String(value);
}
// ============================================================
// Jurisdiction scoping
// ============================================================
/**
 * A rule is in scope when the system is deployed somewhere the rule
 * reaches. Codes are hyphen-delimited most-general-first ('EU',
 * 'US-CO', 'US-NY-NYC'), so a listed jurisdiction is covered by a rule
 * whose code is that code or a prefix of it at a segment boundary:
 *
 *   deployed 'EU-DE'     → EU rules apply          (ADR-0024's EU carve-out)
 *   deployed 'US-NY-NYC' → US-NY and US rules apply
 *   deployed 'US-NY'     → US-NY-NYC rules do NOT apply
 *
 * The last line is the important one: an employer somewhere in New York
 * State is not thereby subject to a New York City ordinance. Narrowing
 * only ever happens by adding a more specific deployment code.
 *
 * No `deployment_jurisdictions` (or an empty list) means "not stated" —
 * every rule is considered, which is the safe direction for an intake
 * form that has not asked the question yet (migration 070).
 */
function jurisdictionInScope(ruleJurisdiction, deployed) {
    if (deployed.length === 0) {
        return { inScope: true, reason: 'Deployment jurisdictions not stated — every rule considered' };
    }
    const rule = ruleJurisdiction.toUpperCase();
    const hit = deployed.find((d) => d === rule || d.startsWith(`${rule}-`));
    if (hit === undefined)
        return { inScope: false };
    return {
        inScope: true,
        reason: hit === rule
            ? `Deployed in ${hit}`
            : `Deployed in ${hit}, which is within ${rule}`,
    };
}
function normalizeJurisdictionList(list) {
    return list.map((j) => j.trim().toUpperCase()).filter((j) => j.length > 0);
}
// ============================================================
// Conflict detection + stricter-wins recommendation (ADR-0024 §5)
// ============================================================
/**
 * Conflicts are only raised between rules from DIFFERENT jurisdictions.
 * UC-D is about regulatory divergence: within one regime, a
 * transparency duty and a high-risk duty stack rather than disagree
 * (EU Art. 50 on top of Annex III), and Art. 73's 2 / 10 / 15-day
 * clocks are outcome tiers of one rule set, not a contradiction.
 * Flagging those would bury the cross-border conflicts the view exists
 * to show.
 */
function detectConflicts(applicable) {
    const conflicts = [];
    for (let i = 0; i < applicable.length; i++) {
        for (let j = i + 1; j < applicable.length; j++) {
            const [x, y] = orderPair(applicable[i], applicable[j]);
            if (x.jurisdiction === y.jurisdiction)
                continue;
            // ---- tier: one per rule pair
            if (x.tier !== null && y.tier !== null && x.tier !== y.tier) {
                const a = sideOf(x);
                const b = sideOf(y);
                conflicts.push({ a, b, kind: 'tier', ...resolve('tier', a, b) });
            }
            // ---- deadline / duty: at most one per shared duty id
            for (const oa of x.obligations) {
                for (const ob of y.obligations) {
                    if (oa.duty !== ob.duty)
                        continue;
                    const kind = obligationConflictKind(oa, ob);
                    if (kind === null)
                        continue;
                    const a = sideOf(x, oa);
                    const b = sideOf(y, ob);
                    conflicts.push({ a, b, kind, ...resolve(kind, a, b) });
                }
            }
        }
    }
    conflicts.sort(compareConflict);
    return conflicts;
}
function obligationConflictKind(a, b) {
    const ma = deadlineMagnitude(a.deadline_rule);
    const mb = deadlineMagnitude(b.deadline_rule);
    if (ma !== null &&
        mb !== null &&
        a.deadline_rule?.kind === b.deadline_rule?.kind &&
        ma !== mb) {
        return 'deadline';
    }
    if (!sameScope(a.scope, b.scope))
        return 'duty';
    return null;
}
function sameScope(a, b) {
    const sa = [...(a ?? [])].sort();
    const sb = [...(b ?? [])].sort();
    return sa.length === sb.length && sa.every((v, i) => v === sb[i]);
}
function isSuperset(a, b) {
    const sa = new Set(a ?? []);
    const sb = b ?? [];
    return sb.length < sa.size && sb.every((v) => sa.has(v));
}
/** Comparable magnitude within a kind. Lower = stricter. */
function deadlineMagnitude(d) {
    if (!d)
        return null;
    if (d.kind === 'duration' || d.kind === 'before_event')
        return d.hours;
    if (d.kind === 'recurring')
        return d.months;
    if (d.kind === 'business_days') {
        // Convert business days to comparable calendar hours so this
        // clock can be ranked against `duration` when they meet in a
        // §5 fall-through (business_days vs duration is treated as
        // "duration" in `resolve` — see the kind check there). Each
        // business day = 24h of clock time on a working day; we
        // approximate a full week as 5 working days plus 2 weekend
        // days of pause, i.e. 5 business days ≈ 168 calendar hours,
        // one business day ≈ 33.6 hours. This over-estimates a
        // 1-BD clock by ~10 hours vs. Mon 9→Tue 9, but the point of
        // the encoding is that "10 business days" (2 calendar weeks
        // + a weekend, i.e. ~336h) is stricter than "14 calendar
        // days" (336h flat) and less strict than "10 calendar days"
        // (240h). ADR-0024 amendment records the exact conversion.
        return Math.round(d.days * (7 / 5) * 24);
    }
    return null;
}
/**
 * ADR-0024 §5 orders the criteria: shortest deadline, broadest duty,
 * highest tier, then `strictness_rank`. Which criteria can decide
 * depends on what the two rules actually disagree about, so the walk
 * starts at the conflict's own kind and falls through the remaining
 * criteria in §5 order. A tie at every criterion recommends neither —
 * it names side `a` for determinism and says so in the reason.
 */
function resolve(kind, a, b) {
    if (kind === 'deadline' && a.deadline_rule && b.deadline_rule) {
        const ma = deadlineMagnitude(a.deadline_rule);
        const mb = deadlineMagnitude(b.deadline_rule);
        if (ma !== null && mb !== null && ma !== mb) {
            // "Shortest deadline" means least time to comply — but a
            // `before_event` clock is advance notice, where MORE hours is the
            // stricter duty. Comparing raw magnitudes both ways round would
            // recommend the weaker notice period.
            const leadTime = a.deadline_rule?.kind === 'before_event';
            const aWins = leadTime ? ma > mb : ma < mb;
            return {
                recommendation: aWins ? 'a' : 'b',
                reason: leadTime
                    ? `Longer advance notice on the same duty (${a.duty}): ` +
                        `${formatDeadline(a.deadline_rule)} (${a.jurisdiction}) vs ` +
                        `${formatDeadline(b.deadline_rule)} (${b.jurisdiction}). ` +
                        `Giving the longer notice satisfies both.`
                    : `Shorter deadline on the same duty (${a.duty}): ` +
                        `${formatDeadline(a.deadline_rule)} (${a.jurisdiction}) vs ` +
                        `${formatDeadline(b.deadline_rule)} (${b.jurisdiction}). ` +
                        `Meeting the shorter clock satisfies both.`,
            };
        }
    }
    if (kind === 'deadline' || kind === 'duty') {
        if (isSuperset(a.scope, b.scope)) {
            return {
                recommendation: 'a',
                reason: `Broader duty on ${a.duty}: ${a.jurisdiction} covers ` +
                    `[${(a.scope ?? []).join(', ')}], ${b.jurisdiction} covers ` +
                    `[${(b.scope ?? []).join(', ')}].`,
            };
        }
        if (isSuperset(b.scope, a.scope)) {
            return {
                recommendation: 'b',
                reason: `Broader duty on ${b.duty}: ${b.jurisdiction} covers ` +
                    `[${(b.scope ?? []).join(', ')}], ${a.jurisdiction} covers ` +
                    `[${(a.scope ?? []).join(', ')}].`,
            };
        }
    }
    if (a.tier !== null && b.tier !== null && a.tier !== b.tier) {
        const aWins = TIER_RANK[a.tier] > TIER_RANK[b.tier];
        return {
            recommendation: aWins ? 'a' : 'b',
            reason: `Higher tier: ${a.jurisdiction} classifies this system as ` +
                `${a.tier}, ${b.jurisdiction} as ${b.tier}. Building to the ` +
                `higher tier satisfies both.`,
        };
    }
    if (a.strictness_rank !== b.strictness_rank) {
        const aWins = a.strictness_rank > b.strictness_rank;
        return {
            recommendation: aWins ? 'a' : 'b',
            reason: `Not otherwise comparable — resolved on the editor-assigned ` +
                `strictness rank (${a.jurisdiction} ${a.strictness_rank} vs ` +
                `${b.jurisdiction} ${b.strictness_rank}).`,
        };
    }
    return {
        recommendation: 'a',
        reason: `Equally strict on every criterion — no recommendation is ` +
            `implied. Both obligations stand and both citations apply.`,
    };
}
function formatDeadline(d) {
    switch (d.kind) {
        case 'none':
            return 'no deadline';
        case 'recurring':
            return d.months === 12 ? 'annually' : `every ${d.months} months`;
        case 'duration':
        case 'before_event': {
            const span = d.hours % 24 === 0 ? `${d.hours / 24} days` : `${d.hours} hours`;
            return d.kind === 'before_event' ? `${span} before` : span;
        }
        case 'business_days':
            return d.days === 1 ? '1 business day' : `${d.days} business days`;
    }
}
function sideOf(rule, obligation) {
    const side = {
        rule_id: rule.rule_id,
        jurisdiction: rule.jurisdiction,
        statute: rule.statute,
        citation: obligation?.citation ?? rule.citation,
        tier: rule.tier,
        ...(rule.tier_term ? { tier_term: rule.tier_term } : {}),
        strictness_rank: rule.strictness_rank,
    };
    if (obligation) {
        side.obligation_id = obligation.obligation_id;
        side.duty = obligation.duty;
        side.deadline_rule = obligation.deadline_rule;
        if (obligation.scope)
            side.scope = [...obligation.scope];
    }
    return side;
}
// ============================================================
// Deterministic ordering
// ============================================================
function compareApplicable(a, b) {
    return (cmp(a.jurisdiction, b.jurisdiction) ||
        cmp(a.statute, b.statute) ||
        cmp(a.citation, b.citation) ||
        cmp(a.rule_id, b.rule_id));
}
function orderPair(x, y) {
    return compareApplicable(x, y) <= 0 ? [x, y] : [y, x];
}
function compareConflict(a, b) {
    return (cmp(a.a.jurisdiction, b.a.jurisdiction) ||
        cmp(a.a.statute, b.a.statute) ||
        cmp(a.a.rule_id, b.a.rule_id) ||
        cmp(a.b.jurisdiction, b.b.jurisdiction) ||
        cmp(a.b.statute, b.b.statute) ||
        cmp(a.b.rule_id, b.b.rule_id) ||
        cmp(a.kind, b.kind) ||
        cmp(a.a.obligation_id ?? '', b.a.obligation_id ?? '') ||
        cmp(a.b.obligation_id ?? '', b.b.obligation_id ?? ''));
}
function cmp(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/**
 * A rule's tier is the strictest tier any of its obligations declares, with
 * the tier term of the first obligation that set it (ADR-0077 §4, display only).
 */
function ruleTierWithTerm(obligations) {
    let best = null;
    let term;
    for (const o of obligations) {
        if (!o.tier)
            continue;
        if (best === null || TIER_RANK[o.tier] > TIER_RANK[best]) {
            best = o.tier;
            term = o.tier_term;
        }
    }
    return term ? { tier: best, tier_term: term } : { tier: best };
}
/**
 * Dates are ISO ('2026-08-02' or a full timestamp). Compare on the date
 * portion so a rule that takes effect "on" the as-of date is in force.
 */
function toDate(value) {
    return value.slice(0, 10);
}
//# sourceMappingURL=jurisdiction-applicability-service.js.map